/**
 * Turns wizard state into event properties.
 *
 * Lives beside the wizard rather than in `src/analytics`: what an answer *means*
 * is this feature's business, and the analytics module must not learn the shape
 * of `OnboardingDraft`.
 *
 * What an answer *is* no longer travels from here. The platform records every
 * answered step itself, out of `set_step` — so a step's contents reach analytics
 * as the platform's own event, and this file is left with the two properties
 * that have to ride along on *later* client events.
 */
import type { SuperProperties } from 'src/analytics'

import type { WizardStepKey } from './constants'
import type { OnboardingDraft } from './types'

/**
 * Answers that must ride on **every later event**, not just the step that
 * collected them.
 *
 * BR-37 lists company size and industry among the properties every event carries,
 * and BR-11 makes company size a primary reporting dimension. Sent only as a
 * property of the step that asked, they would be unusable as a breakdown: a
 * funnel breaks down by the value on its *first* step, and `onboarding_started`
 * happens before either question is answered. So they are promoted to super
 * properties the moment the step completes.
 *
 * Returns an empty object for steps that answer nothing global — `registerProperties`
 * ignores it.
 */
export const journeyProperties = (
  step: WizardStepKey,
  draft: OnboardingDraft
): SuperProperties => {
  switch (step) {
    case 'details':
      return { company_size: draft.companySize ?? undefined }
    case 'template':
      // The codename, which is what the profile stores and what a chart legend
      // has to show. `no_data` — the clean-workspace tile — registers like any
      // other value: "seeded from nothing" is an answer, not a missing one.
      return { industry: draft.industry ?? undefined }
    default:
      return {}
  }
}

/**
 * Shortens a validation i18n key to its leaf, e.g.
 * `workspaces.onboarding.errors.nameRequired` → `nameRequired`.
 *
 * The full key is stable but unreadable in a chart legend, and the prefix is the
 * same on every one of them.
 */
export const validationErrorCode = (key: string): string =>
  key.split('.').pop() ?? key

/**
 * The step the wizard is on, or null on the terminal `done` screen — which is
 * not a step and must not be reported as one.
 *
 * Returns `WizardStepKey`, not `CoreStepKey`: it is assignable to `CoreStepKey`
 * for the analytics calls, and staying narrow means callers can switch on it
 * exhaustively.
 */
export const wizardStepOf = (
  step: OnboardingDraft['step']
): WizardStepKey | null => (step === 'done' ? null : step)
